/**
 * TARGET ASSIST — melee suck-to-target, the God-of-War recipe scaled to our reach
 * (GDC 2019 "Evolving Combat in God of War": every attack steers onto a scored target;
 * pure camera-reticle selection "felt clunky"; LATERAL suck disoriented, so range decays
 * with angle off camera-forward). This is the single feature that kills "I pressed attack
 * and missed" — a swing near an enemy is a swing AT that enemy.
 *
 * Selection is scored, not nearest-wins: a close target dead-ahead beats a closer one at
 * the edge of the cone. Everything is evaluated in the attacker's TANGENT PLANE (radial
 * up removed) — melee is a ground game, and a flyer 20 m overhead must not vacuum the
 * player off a cliff; a modest radial gate keeps the assist honest.
 *
 * This module only PICKS; the steering itself (clamped auto-face + arrival-sized lunge)
 * lives on the CharacterController so the physics stays in one place.
 */
import type * as THREE from "three";

/** Assist acquisition range (m) for a target dead-ahead. Beyond it a swing is a whiff the
 *  player earned. Decays with angle — see EDGE_RANGE_K. */
const ASSIST_RANGE = 9;
/** Cone half-angle (rad, ~75°). Outside it there is NO assist at all — swinging away from
 *  an enemy on purpose must stay possible (kiting, hitting a barrel behind you...). */
const ASSIST_CONE = 1.3;
/** Fraction of ASSIST_RANGE left at the cone's EDGE (the GoW angle-scaled range): a target
 *  at 75° must be much closer than one dead-ahead to grab the swing. */
const EDGE_RANGE_K = 0.4;
/** Radial (up/down) gate (m): a target this far off the attacker's ground plane is not a
 *  melee target, whatever the horizontal math says. */
const RADIAL_GATE = 4;

export interface AssistPick {
  /** System-space position of the chosen target (a live ref — read it immediately). */
  pos: THREE.Vector3;
  /** Tangent-plane distance (m) and angle off camera-forward (rad) — the lunge inputs. */
  dist: number;
  angle: number;
}

const _pick: AssistPick = { pos: null as unknown as THREE.Vector3, dist: 0, angle: 0 };

/**
 * Score every candidate and return the best, or null (→ caller keeps the un-assisted swing).
 * `origin`/`up` are the attacker's system-space position + radial up; `fwd` is CAMERA forward
 * (any length; components off the tangent plane are ignored). Candidates are anything with a
 * system-space `pos` — today the live drone views.
 */
export function pickAssistTarget(
  origin: THREE.Vector3,
  up: THREE.Vector3,
  fwd: THREE.Vector3,
  candidates: Iterable<{ pos: THREE.Vector3 }>,
): AssistPick | null {
  // Tangent-plane camera forward (normalized in 2D "plane space" via components below).
  const fUp = fwd.x * up.x + fwd.y * up.y + fwd.z * up.z;
  let fx = fwd.x - fUp * up.x, fy = fwd.y - fUp * up.y, fz = fwd.z - fUp * up.z;
  const fLen = Math.hypot(fx, fy, fz) || 1;
  fx /= fLen; fy /= fLen; fz /= fLen;

  let best: { pos: THREE.Vector3; dist: number; angle: number } | null = null;
  let bestScore = 0;
  for (const c of candidates) {
    const dx = c.pos.x - origin.x, dy = c.pos.y - origin.y, dz = c.pos.z - origin.z;
    const radial = dx * up.x + dy * up.y + dz * up.z;
    if (Math.abs(radial) > RADIAL_GATE) continue;
    const tx = dx - radial * up.x, ty = dy - radial * up.y, tz = dz - radial * up.z;
    const dist = Math.hypot(tx, ty, tz);
    if (dist < 1e-3 || dist > ASSIST_RANGE) continue;
    const cos = (tx * fx + ty * fy + tz * fz) / dist;
    const angle = Math.acos(Math.max(-1, Math.min(1, cos)));
    if (angle > ASSIST_CONE) continue;
    // Angle-scaled range: the admissible distance shrinks toward the cone edge.
    const effRange = ASSIST_RANGE * (1 - (1 - EDGE_RANGE_K) * (angle / ASSIST_CONE));
    if (dist > effRange) continue;
    const score = (1 - dist / effRange) * (1 - angle / ASSIST_CONE) + 0.15 * (1 - dist / ASSIST_RANGE);
    if (score > bestScore) {
      bestScore = score;
      best = { pos: c.pos, dist, angle };
    }
  }
  if (!best) return null;
  _pick.pos = best.pos;
  _pick.dist = best.dist;
  _pick.angle = best.angle;
  return _pick;
}
